import * as d3 from 'd3';
import React, {useState} from 'react';

import makeCancelable from 'lib/makeCancelable';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {Table} from 'notebook/tables/TableConstants';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as net from 'util/Net';

interface CSVFilePreviewProps {
  url: string;
  maxRows?: number;
}

export default function CSVFilePreview(props: CSVFilePreviewProps): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<Array<Array<string>> | null>(null);

  useOnMount(() => {
    const filePromise = makeCancelable(Promise.resolve(net.get(props.url)));
    setIsLoading(true);
    filePromise.promise
      .then((data) => {
        const newRows = d3.csvParseRows(data);
        setRows(newRows);
        setIsLoading(false);
      })
      .catch((response) => {
        if (response.isCanceled) {
          return;
        }
        setIsLoading(false);
      });
  });

  function _createTable(): Table {
    const header = rows![0];
    return {
      name: 'CSV Preview',
      table: rows!
        .slice(0, props.maxRows ? props.maxRows + 1 : undefined)
        .map((row: Array<string>) => row.map((text) => ({text}))),
      widths: header.map(() => 120),
      columnConstraints: header.map(() => null),
      headerRow: false,
      headerCol: false,
      columnLabels: header.map(() => null),
    };
  }

  if (isLoading) {
    return <CenteredHorizontalThrobber />;
  }
  if (!rows) {
    return (
      <p className="u-spacing-top faded text-center">
        An error occurred while loading the file. Please try again.
      </p>
    );
  }
  if (rows.length === 0) {
    return <p className="u-spacing-top faded text-center">The file is empty.</p>;
  }
  return (
    <div className="mediocre">
      <SimpleTableEditable
        {...SimpleTableEditable.defaultProps}
        {...standaloneProps}
        table={_createTable()}
        cid="csvFilePreview"
        suppressTitleBar={true}
        hideFormulaBox={true}
        isCompactBottomSpacing={true}
        locked={true}
      />
    </div>
  );
}
